"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createBasicGraphUtils = void 0;
const common_1 = require("@finley-technologies/common");
const createBasicGraphUtils = ({ artifactDocumentId, version, artifacts, cfIdToSlugMap, lpIdToSlugMap, dependencies, }) => {
    const prefix = `[artifactDocumentId:${artifactDocumentId}]`;
    const getArtifactById = (artifactId) => {
        const foundArtifacts = artifacts.filter((artifact) => artifact.id === artifactId);
        (0, common_1.finleyAssert)(foundArtifacts.length === 1, `${prefix}: [artifactId:${artifactId}] found [${foundArtifacts.length}] matches]`);
        return foundArtifacts[0];
    };
    // returns direct outputs of the given artifactId
    const getOutputs = (artifactId) => {
        return dependencies
            .filter(({ inputArtifactId }) => inputArtifactId === artifactId)
            .map(({ outputArtifactId }) => outputArtifactId);
    };
    // returns direct inputs of the given artifactId
    const getInputs = (artifactId) => {
        return dependencies
            .filter(({ outputArtifactId }) => outputArtifactId === artifactId)
            .map(({ inputArtifactId }) => inputArtifactId);
    };
    // returns connections to the given artifactId
    const getDirectConnections = (artifactId) => {
        return dependencies
            .filter(({ inputArtifactId, outputArtifactId }) => inputArtifactId === artifactId || outputArtifactId === artifactId)
            .map(({ inputArtifactId, outputArtifactId }) => inputArtifactId === artifactId ? outputArtifactId : inputArtifactId);
    };
    // returns all outputs of the given artifactId
    const getRecursiveOutputs = (artifactId) => {
        const directDependents = getOutputs(artifactId);
        const recursiveDependents = directDependents.flatMap((dependent) => getRecursiveOutputs(dependent));
        return Array.from(new Set([...recursiveDependents, ...directDependents]));
    };
    // returns all inputs for the given artifactId
    const getRecursiveInputs = (artifactId) => {
        const directInputs = getInputs(artifactId);
        const recursiveInputs = directInputs.flatMap((input) => getRecursiveInputs(input));
        return Array.from(new Set([...directInputs, ...recursiveInputs]));
    };
    const hasArtifact = ({ artifactId }) => {
        const found = artifacts.find((artifact) => artifact.id === artifactId);
        return !!found;
    };
    const getArtifactSlug = ({ artifactId }) => {
        return getArtifactById(artifactId).artifactSlug;
    };
    const getOutputArtifacts = (artifactId) => getOutputs(artifactId).map(getArtifactById);
    const getInputArtifacts = (artifactId) => getInputs(artifactId).map(getArtifactById);
    const cfIdToSlug = (id) => {
        const slug = cfIdToSlugMap.get(id);
        (0, common_1.finleyAssert)(slug, `${prefix}: [creditFacilityId:${id}] is not referenced in the graph`);
        return slug;
    };
    const getCFSlugs = () => Array.from(cfIdToSlugMap.values());
    const getCFIds = () => Array.from(cfIdToSlugMap.keys());
    const getLPSlugs = () => Array.from(lpIdToSlugMap.values());
    const getLPIds = () => Array.from(lpIdToSlugMap.keys());
    const lpIdToSlug = (id) => {
        const slug = lpIdToSlugMap.get(id);
        (0, common_1.finleyAssert)(slug, `${prefix}: [loanProductId:${id}] is not referenced in the graph`);
        return slug;
    };
    const lpSlugToId = (slug) => {
        const id = Array.from(lpIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
        (0, common_1.finleyAssert)(id, `${prefix}: [loanProductSlug:${slug}] is not referenced in the graph`);
        return id;
    };
    const cfSlugToId = (slug) => {
        const id = Array.from(cfIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
        (0, common_1.finleyAssert)(id, `${prefix}: [creditFacilitySlug:${slug}] is not referenced in the graph`);
        return id;
    };
    /**
     * ArtifactWithSlugs => Artifact
     */
    const toArtifact = (artifactWithSlugs) => {
        const { creditFacilitySlug, loanProductSlug, ...rest } = artifactWithSlugs;
        const creditFacilityId = creditFacilitySlug
            ? cfSlugToId(creditFacilitySlug)
            : null;
        const loanProductId = loanProductSlug ? lpSlugToId(loanProductSlug) : null;
        return {
            ...rest,
            creditFacilityId,
            loanProductId,
        };
    };
    // walk all the dependencies and split the graph into
    // ConnectedGraph - which is an array of artifactIds that are connected.
    const splitDisjointGraphs = () => {
        // graphs is array of graphs.
        const graphs = [];
        const visited = new Set();
        const dfs = (node, graph = []) => {
            if (!visited.has(node)) {
                visited.add(node);
                graph.push(node);
                const connections = getDirectConnections(node);
                connections.forEach((connection) => {
                    dfs(connection, graph);
                });
            }
            return graph;
        };
        artifacts.forEach(({ id }) => {
            if (!visited.has(id)) {
                const graph = dfs(id);
                graphs.push(graph);
            }
        });
        return graphs;
    };
    const bfsTraversal = ({ startNodes, criteria, search = 'outputs', }) => {
        const visitedNodes = new Set();
        const queue = [...startNodes];
        const matchingNodes = [];
        // Iterate through the queue until it's empty
        // eslint-disable-next-line no-restricted-syntax
        while (queue.length > 0) {
            const currentNode = queue.shift();
            (0, common_1.finleyAssert)(currentNode, `BFS error: queue is empty unexpectedly`);
            if (visitedNodes.has(currentNode)) {
                continue; // Skip if we've already visited this node
            }
            visitedNodes.add(currentNode);
            const { matches, continueBranch } = criteria(currentNode);
            if (matches) {
                matchingNodes.push(currentNode);
            }
            if (!continueBranch) {
                continue; // Skip adding descendants if we're not continuing in this branch
            }
            const connections = [];
            switch (search) {
                case 'outputs':
                    connections.push(...getOutputs(currentNode));
                    break;
                case 'inputs':
                    connections.push(...getInputs(currentNode));
                    break;
                case 'both':
                    connections.push(...getOutputs(currentNode), ...getInputs(currentNode));
                    break;
                default:
                    (0, common_1.finleyAssertUnreachable)(search);
            }
            // Add unvisited connections to the queue
            connections.forEach((connectedNode) => {
                if (!visitedNodes.has(connectedNode)) {
                    queue.push(connectedNode);
                }
            });
        }
        return matchingNodes;
    };
    return {
        getArtifactDocumentId: () => artifactDocumentId,
        getArtifactDocumentVersion: () => version,
        getAllArtifacts: () => artifacts,
        cfIdToSlug,
        cfSlugToId,
        lpIdToSlug,
        lpSlugToId,
        getCFSlugs,
        getCFIds,
        getLPSlugs,
        getLPIds,
        hasArtifact,
        getArtifactSlug,
        getOutputs,
        getInputs,
        bfsTraversal,
        getDirectConnections,
        getRecursiveOutputs,
        getRecursiveInputs,
        splitDisjointGraphs,
        getArtifactById,
        getInputArtifacts,
        getOutputArtifacts,
        toArtifact,
    };
};
exports.createBasicGraphUtils = createBasicGraphUtils;
//# sourceMappingURL=dbBasicGraphUtils.js.map